import {
  AccountActivityPage,
  AccountingDashboardPage,
  AccountingPeriodsSettingsPage,
  AccountingSettingsPage,
  AccountsPayablePage,
  ChartOfAccountsPage,
  CogsReportPage,
  DeferredRevenuePage,
  ExpensesReportPage,
  GeneralLedgerPage,
  JournalEntriesPage,
  PaymentMappingsSettingsPage,
  ProfitAndLossPage,
  TaxRatesSettingsPage,
  TaxRefundsSettingsPage,
  TaxReportPage,
  TrialBalancePage,
} from '@components/dashboard/accounting/AccountingPages';
import { AccountingGuidePage } from '@components/dashboard/accounting/guide/AccountingGuidePage';
import { AccessControlCode } from '@shared/constants';
import { Navigate, Route } from 'react-router-dom';
import { AccountingPage } from '../pages/AccountingPage';
import { AccessControlledDashboardPage } from './featureRouteHelpers';

export const createAccountingRoutes = () => (
  <Route path="/accounting" element={<AccountingPage />}>
    <Route index element={<Navigate to="/accounting/dashboard" replace />} />
    <Route path="guide" element={<AccountingGuidePage />} />
    <Route
      path="dashboard"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
          title="Accounting access restricted"
          description="Your account does not currently have permission to access accounting."
        >
          <AccountingDashboardPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="chart-of-accounts"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_VIEW_ACCOUNTS}
          title="Accounting access restricted"
          description="Your account does not currently have permission to view the chart of accounts."
        >
          <ChartOfAccountsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="journals"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_VIEW_JOURNALS}
          title="Accounting access restricted"
          description="Your account does not currently have permission to view journal entries."
        >
          <JournalEntriesPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="payables"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
          title="Accounting access restricted"
          description="Your account does not currently have permission to access accounts payable."
        >
          <AccountsPayablePage />
        </AccessControlledDashboardPage>
      }
    />
    <Route path="reports">
      <Route
        index
        element={<Navigate to="/accounting/reports/trial-balance" replace />}
      />
      <Route
        path="trial-balance"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <TrialBalancePage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="general-ledger"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <GeneralLedgerPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="account-activity"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <AccountActivityPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="profit-and-loss"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <ProfitAndLossPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="tax-report"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <TaxReportPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="cogs-report"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <CogsReportPage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="deferred-revenue"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <DeferredRevenuePage />
          </AccessControlledDashboardPage>
        }
      />
      <Route
        path="expenses"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.ACCOUNTING_VIEW_REPORTS}
            title="Accounting reports access restricted"
            description="Your account does not currently have permission to view accounting reports."
          >
            <ExpensesReportPage />
          </AccessControlledDashboardPage>
        }
      />
    </Route>
    <Route
      path="settings"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_MANAGE_PAYMENT_MAPPINGS}
          title="Accounting settings access restricted"
          description="Your account does not currently have permission to manage posting rules."
        >
          <AccountingSettingsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="settings/tax-rates"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_MANAGE_TAX_RATES}
          title="Accounting settings access restricted"
          description="Your account does not currently have permission to manage tax rates."
        >
          <TaxRatesSettingsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="settings/accounting-periods"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_CLOSE_PERIODS}
          title="Accounting settings access restricted"
          description="Your account does not currently have permission to manage accounting periods."
        >
          <AccountingPeriodsSettingsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="settings/payment-mappings"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_MANAGE_PAYMENT_MAPPINGS}
          title="Accounting settings access restricted"
          description="Your account does not currently have permission to manage payment mappings."
        >
          <PaymentMappingsSettingsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="settings/tax-refunds"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.ACCOUNTING_MANAGE_TAX_RATES}
          title="Accounting settings access restricted"
          description="Your account does not currently have permission to manage tax refunds."
        >
          <TaxRefundsSettingsPage />
        </AccessControlledDashboardPage>
      }
    />
  </Route>
);
